import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../auth/firebase";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { useAuth } from "../../auth/AuthContext";
import { useQueryParams } from "../../providers/query-params-provider";
import { useDemographies } from "../../providers/demographies-provider";

const GoogleIcon = () => (
  <span className="w-5 h-5 mr-2 bg-blue-200 rounded-full inline-block">G</span>
);
const backgroundImage = "/images/background/onboard.png";

export function AuthScreen({ isInternal = false }) {
  const [email, setEmail] = useState("");
  const { refresh: refreshDemographies } = useDemographies();
  const [userCreated, setUserCreated] = useState(false);
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState("en");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUserContext, user } = useAuth();
  const [searchParams] = useSearchParams();
  const { utm_source, utm_campaign, utm_medium, af } = useQueryParams();

  useEffect(() => {
    if (user?.uid || userCreated) {
      const apiUrl = process.env.REACT_APP_API_URL;
      const getUserUrl = `${apiUrl}/users/${user?.email}/`;
      const request = fetch(getUserUrl, {
        method: "GET",
        headers: {
          Authorization: `bearer ${user?.accessToken}`,
        },
      });
      request.then((response) => {
        if (response.ok) {
            return refreshDemographies();
        }
      }).then(() => {
          navigate("/add-demography");
      })
    }
  }, [user, userCreated]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    const provider = new GoogleAuthProvider();

    try {
      // Capture query params
      let query_param = "";

      if (utm_source) {
        query_param += `utm_source=${utm_source}&`;
      }
      if (utm_campaign) {
        query_param += `utm_campaign=${utm_campaign}&`;
      }
      if (utm_medium) {
        query_param += `utm_medium=${utm_medium}&`;
      }
      if (af) {
        query_param += `af=${af}&`;
      }

      const credentials = await signInWithPopup(auth, provider);
      // Extract user email from the authenticated user
      const userEmail = credentials.user.email;

      // Define the API endpoint and construct the user data
      const apiUrl = process.env.REACT_APP_API_URL;
      const createUserUrl = `${apiUrl}/users/${query_param ? "?" + query_param : ""}`;

      // Make a POST request to the backend to create the user
      const response = await fetch(createUserUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${credentials?.user?.accessToken}`,
        },
        body: JSON.stringify({ email: userEmail, is_internal: isInternal }),
      });

      if (!response.ok) {
        throw new Error("Failed to create user on the backend.");
      }

      const userData = await response.json();

      setUserContext({
        id: userData.id,
        uid: credentials.user.uid,
        email: userData.email,
      });


      // Populate demographies in provider before navigating (non-blocking)
      try {
        await refreshDemographies();
      } catch (e) {
        // Non-blocking: log error but continue navigation
        console.error("Failed to refresh demographies:", e);
      }

      navigate("/add-demography");
      setUserCreated(true);
    } catch (err) {
      setError(err.message || "Google sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-6"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPositionX: "center",
      }}
    >
      <div className="w-full max-w-sm bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Please Authenticate
        </h2>

        <div className="space-y-3">
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <GoogleIcon />
            Google
          </button>
        </div>
      </div>
    </div>
  );
}
